/**
 * Runtime validator for assistant stream frames.
 *
 * Given a raw, already-JSON-parsed frame of unknown shape, decide whether it
 * is a well-formed frame the reducer can apply. Dependency-light on purpose:
 * hand-rolled structural checks, no JSON-Schema runtime pulled in. The reducer
 * RECOVERS (fresh snapshot) on any `ok:false`, so validation is a gate, not a
 * parser: it verifies the discriminant plus the concrete fields the reducer
 * trusts.
 *
 * Outcomes:
 *   - `unknown-type`   -> a durable-looking envelope whose `type` is not one we
 *                        know (forward-compat: the reducer resyncs).
 *   - `schema-invalid` -> recognised family but malformed / missing fields.
 */
import {
  DURABLE_EVENT_TYPES,
  LIVE_DELTA_TYPE,
  LIVE_TOOL_STARTED_TYPE,
  type AssistantFrame,
  type ConfirmationExpiredData,
  type ConfirmationResolvedData,
  type DiffEntryView,
  type DiffValue,
  type DurableEvent,
  type DurableEventType,
  type LiveDelta,
  type LiveToolStarted,
  type LocalizedFallback,
  type MessageCompletedData,
  type MessageCompletedRedacted,
  type ModelCompletedData,
  type ProposalDiffView,
  type ProposalView,
  type RedactedMarker,
  type RunCancelledData,
  type RunCompletedData,
  type RunExpiredData,
  type RunFailedData,
  type RunQueuedData,
  type RunRequeuedData,
  type RunStartedData,
  type Sensitivity,
  type SnapshotFrame,
  type SnapshotGeneration,
  type SnapshotMessage,
  type SnapshotState,
  type SnapshotToolCard,
  type ToolStartedData,
  type ToolCompletedData,
  type ToolFailedData,
} from "./generated-events";

export type ValidateFrameResult =
  | { ok: true; frame: AssistantFrame }
  | { ok: false; reason: "unknown-type" | "schema-invalid" };

const DURABLE_TYPE_SET: ReadonlySet<string> = new Set<string>(DURABLE_EVENT_TYPES);
const SENSITIVITY_VALUES: ReadonlySet<string> = new Set<string>([
  "NORMAL",
  "SENSITIVE",
  "FINANCIAL",
]);

type Obj = Record<string, unknown>;

function isObject(v: unknown): v is Obj {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
function isString(v: unknown): v is string {
  return typeof v === "string";
}
function isInt(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v);
}
function isBoolean(v: unknown): v is boolean {
  return typeof v === "boolean";
}
function isSensitivity(v: unknown): v is Sensitivity {
  return typeof v === "string" && SENSITIVITY_VALUES.has(v);
}
function isDurableEventType(v: string): v is DurableEventType {
  return DURABLE_TYPE_SET.has(v);
}
function hasOnlyKeys(data: Obj, allowed: readonly string[]): boolean {
  return Object.keys(data).every((key) => allowed.includes(key));
}

function isRedactedMarker(data: unknown): data is RedactedMarker {
  if (!isObject(data)) return false;
  return (
    hasOnlyKeys(data, ["redacted", "type", "reason"]) &&
    data.redacted === true &&
    isString(data.type) &&
    data.reason === "insufficient_permissions"
  );
}

function isLocalizedFallback(data: unknown): data is LocalizedFallback {
  return (
    isObject(data) && hasOnlyKeys(data, ["en", "ar"]) && isString(data.en) && isString(data.ar)
  );
}

function isValueState(data: unknown): data is DiffValue["state"] {
  return data === "VALUE" || data === "NULL" || data === "MASKED";
}

function optionalStringOrNull(data: unknown): boolean {
  return data === undefined || isString(data) || data === null;
}
function optionalNumberOrNull(data: unknown): boolean {
  return data === undefined || typeof data === "number" || data === null;
}
function optionalBooleanOrNull(data: unknown): boolean {
  return data === undefined || isBoolean(data) || data === null;
}

/** Max nesting for a list DiffValue. Deeper than this fails validation (→ the
 *  reducer recovers) instead of overflowing the stack — a JSON-parsed, deeply
 *  nested payload must never be able to crash the client. */
const MAX_DIFF_VALUE_DEPTH = 32;

function isDiffValue(data: unknown): data is DiffValue {
  return isDiffValueAtDepth(data, 0);
}

function isDiffValueAtDepth(data: unknown, depth: number): data is DiffValue {
  if (depth > MAX_DIFF_VALUE_DEPTH) return false;
  if (!isObject(data) || !isString(data.type) || !isValueState(data.state)) return false;

  switch (data.type) {
    case "money":
      return (
        hasOnlyKeys(data, ["type", "state", "amount", "currency"]) &&
        optionalStringOrNull(data.amount) &&
        optionalStringOrNull(data.currency)
      );
    case "number":
      return (
        hasOnlyKeys(data, ["type", "state", "value", "unit"]) &&
        optionalNumberOrNull(data.value) &&
        optionalStringOrNull(data.unit)
      );
    case "date":
    case "time":
    case "text":
      return hasOnlyKeys(data, ["type", "state", "value"]) && optionalStringOrNull(data.value);
    case "enum":
      return (
        hasOnlyKeys(data, ["type", "state", "code", "labelKey", "labelFallback"]) &&
        optionalStringOrNull(data.code) &&
        optionalStringOrNull(data.labelKey) &&
        (data.labelFallback === undefined ||
          data.labelFallback === null ||
          isLocalizedFallback(data.labelFallback))
      );
    case "entityRef":
      return (
        hasOnlyKeys(data, ["type", "state", "entityType", "id", "displayText"]) &&
        optionalStringOrNull(data.entityType) &&
        optionalStringOrNull(data.id) &&
        optionalStringOrNull(data.displayText)
      );
    case "boolean":
      return hasOnlyKeys(data, ["type", "state", "value"]) && optionalBooleanOrNull(data.value);
    case "list":
      return (
        hasOnlyKeys(data, ["type", "state", "items"]) &&
        (data.items === undefined ||
          data.items === null ||
          (Array.isArray(data.items) &&
            data.items.every((item) => isDiffValueAtDepth(item, depth + 1))))
      );
    default:
      return false;
  }
}

function isDiffOperation(data: unknown): data is DiffEntryView["operation"] {
  return data === "ADD" || data === "REPLACE" || data === "REMOVE";
}

function isDisplayPolicy(data: unknown): data is DiffEntryView["displayPolicy"] {
  return data === "FULL" || data === "MASKED" || data === "OMITTED";
}

function isDiffEntryView(data: unknown): data is DiffEntryView {
  return (
    isObject(data) &&
    hasOnlyKeys(data, [
      "path",
      "operation",
      "labelKey",
      "labelFallback",
      "before",
      "after",
      "sensitivity",
      "displayPolicy",
      "group",
    ]) &&
    isString(data.path) &&
    isDiffOperation(data.operation) &&
    isString(data.labelKey) &&
    isLocalizedFallback(data.labelFallback) &&
    (data.before === null || isDiffValue(data.before)) &&
    (data.after === null || isDiffValue(data.after)) &&
    isSensitivity(data.sensitivity) &&
    isDisplayPolicy(data.displayPolicy) &&
    // CC-481: optional, nullable, one known member. Any other value is a
    // contract drift and the frame is rejected like any other malformed entry.
    (data.group === undefined || data.group === null || data.group === "current")
  );
}

function isProposalDiffView(data: unknown): data is ProposalDiffView {
  return (
    isObject(data) &&
    hasOnlyKeys(data, ["schemaVersion", "entries"]) &&
    isInt(data.schemaVersion) &&
    Array.isArray(data.entries) &&
    data.entries.every(isDiffEntryView)
  );
}

function isStringArray(data: unknown): data is string[] {
  return Array.isArray(data) && data.every(isString);
}

function isProposalView(data: unknown): data is ProposalView {
  return (
    isObject(data) &&
    hasOnlyKeys(data, [
      "confirmationId",
      "proposalId",
      "commandType",
      "proposalHash",
      "expiresAt",
      "status",
      "diff",
      "warnings",
      "sideEffects",
      // CC-447 pre-localized card title. A display string must not be able to
      // fail the frame into resync (the CC-358 lesson): allowed, optional,
      // type-checked only.
      "title",
    ]) &&
    (data.title === undefined || isString(data.title)) &&
    isString(data.confirmationId) &&
    isString(data.proposalId) &&
    isString(data.commandType) &&
    isString(data.proposalHash) &&
    isString(data.expiresAt) &&
    isString(data.status) &&
    isProposalDiffView(data.diff) &&
    isStringArray(data.warnings) &&
    isStringArray(data.sideEffects)
  );
}

function isSnapshotMessage(data: unknown): data is SnapshotMessage {
  return (
    isObject(data) &&
    // `choices` (CC-357) / `blocks` (CC-358) are allowed but intentionally NOT
    // shape-checked here — a malformed presentation control must not fail the
    // frame into resync. `sanitizeChoices` / `sanitizeBlocks` gate what actually
    // renders at projection time.
    hasOnlyKeys(data, [
      "id",
      "seq",
      "role",
      "text",
      "sensitivity",
      "redacted",
      "choices",
      "blocks",
    ]) &&
    isString(data.id) &&
    isInt(data.seq) &&
    isString(data.role) &&
    (isString(data.text) || data.text === null) &&
    isSensitivity(data.sensitivity) &&
    isBoolean(data.redacted)
  );
}

function isSnapshotGeneration(data: unknown): data is SnapshotGeneration {
  return (
    isObject(data) &&
    hasOnlyKeys(data, [
      "messageId",
      "generationId",
      "streamOrdinal",
      "text",
      "sensitivity",
      "redacted",
      "incomplete",
    ]) &&
    isString(data.messageId) &&
    isString(data.generationId) &&
    isInt(data.streamOrdinal) &&
    data.streamOrdinal >= 0 &&
    (isString(data.text) || data.text === null) &&
    isSensitivity(data.sensitivity) &&
    isBoolean(data.redacted) &&
    data.incomplete === true
  );
}

/**
 * CC-426 legacy normalization (spec A1 — PERMANENT, not a flag-window shim).
 * Pre-CC-426 durable events and snapshot cards carried `{id, tool, ...}` and
 * are persisted in stored conversations forever: any resync of an old
 * conversation replays them, so a strict new-shape validator would loop
 * `schema-invalid → recover → resync → replay`. Rewrites a legacy payload IN
 * PLACE (we own the JSON.parse output) to the new shape: `id` → `invocationId`,
 * `label` → null (the raw `tool` id must NEVER stand in for a label — it leaks
 * English into Arabic transcripts), then drops the legacy keys. Guards below
 * validate the new shape only.
 */
function normalizeLegacyToolPayload(data: unknown): void {
  if (!isObject(data)) return;
  if ("invocationId" in data || data.redacted === true) return;
  if ("id" in data && isString(data.id)) {
    data.invocationId = data.id;
    delete data.id;
  } else if ("tool" in data && !("id" in data)) {
    // Legacy tool.failed carried no stable id at all.
    data.invocationId = null;
  }
  if (!("label" in data)) data.label = null;
  delete data.tool;
}

function isSnapshotToolCard(data: unknown): data is SnapshotToolCard {
  return (
    isObject(data) &&
    hasOnlyKeys(data, ["invocationId", "label", "sensitivity"]) &&
    isString(data.invocationId) &&
    (isString(data.label) || data.label === null) &&
    isSensitivity(data.sensitivity)
  );
}

function isSnapshotState(data: unknown): data is SnapshotState {
  return (
    isObject(data) &&
    hasOnlyKeys(data, [
      "status",
      "messages",
      "currentGeneration",
      "toolCards",
      "factCards",
      "proposal",
      "terminalError",
    ]) &&
    isString(data.status) &&
    Array.isArray(data.messages) &&
    data.messages.every(isSnapshotMessage) &&
    (data.currentGeneration === null || isSnapshotGeneration(data.currentGeneration)) &&
    Array.isArray(data.toolCards) &&
    data.toolCards.every(isSnapshotToolCard) &&
    Array.isArray(data.factCards) &&
    data.factCards.length === 0 &&
    (data.proposal === null || isProposalView(data.proposal)) &&
    (data.terminalError === null || isString(data.terminalError))
  );
}

function isRunQueuedData(data: unknown): data is RunQueuedData {
  return isObject(data) && hasOnlyKeys(data, ["status"]) && isString(data.status);
}
function isRunStartedData(data: unknown): data is RunStartedData {
  return isObject(data) && hasOnlyKeys(data, ["status"]) && data.status === "RUNNING";
}
function isRunRequeuedData(data: unknown): data is RunRequeuedData {
  return isObject(data) && hasOnlyKeys(data, ["reason"]) && isString(data.reason);
}
function isRunCompletedData(data: unknown): data is RunCompletedData {
  return isObject(data) && hasOnlyKeys(data, ["status"]) && isString(data.status);
}
function isRunFailedData(data: unknown): data is RunFailedData {
  return isObject(data) && hasOnlyKeys(data, ["errorCode"]) && isString(data.errorCode);
}
function isRunCancelledData(data: unknown): data is RunCancelledData {
  return (
    isObject(data) &&
    hasOnlyKeys(data, ["status", "reason", "errorCode"]) &&
    (data.status === undefined || isString(data.status)) &&
    (data.reason === undefined || isString(data.reason)) &&
    (data.errorCode === undefined || isString(data.errorCode))
  );
}
function isRunExpiredData(data: unknown): data is RunExpiredData {
  return isObject(data) && hasOnlyKeys(data, ["errorCode"]) && isString(data.errorCode);
}
function isModelCompletedData(data: unknown): data is ModelCompletedData {
  return (
    isObject(data) &&
    hasOnlyKeys(data, ["iteration", "stopReason"]) &&
    isInt(data.iteration) &&
    isString(data.stopReason)
  );
}
function isToolCompletedData(data: unknown): data is ToolCompletedData {
  return (
    isObject(data) &&
    hasOnlyKeys(data, ["invocationId", "label", "sensitivity"]) &&
    isString(data.invocationId) &&
    (isString(data.label) || data.label === null) &&
    isSensitivity(data.sensitivity)
  );
}
function isToolFailedData(data: unknown): data is ToolFailedData {
  return (
    isObject(data) &&
    hasOnlyKeys(data, ["invocationId", "label", "error"]) &&
    (isString(data.invocationId) || data.invocationId === null) &&
    (isString(data.label) || data.label === null) &&
    isString(data.error)
  );
}
function isToolStartedData(data: unknown): data is ToolStartedData {
  return (
    isObject(data) &&
    hasOnlyKeys(data, ["invocationId", "label"]) &&
    isString(data.invocationId) &&
    isString(data.label)
  );
}
function isConfirmationResolvedData(data: unknown): data is ConfirmationResolvedData {
  return isObject(data) && hasOnlyKeys(data, ["status"]) && isString(data.status);
}
function isConfirmationExpiredData(data: unknown): data is ConfirmationExpiredData {
  return isObject(data) && hasOnlyKeys(data, ["confirmationId"]) && isString(data.confirmationId);
}
function isMessageCompletedData(data: unknown): data is MessageCompletedData {
  return (
    isObject(data) &&
    // `choices` (CC-357) / `blocks` (CC-358) allowed but NOT shape-checked — see
    // isSnapshotMessage; sanitize decides what renders. Absence still passes.
    hasOnlyKeys(data, [
      "messageId",
      "generationId",
      "messageSeq",
      "text",
      "sensitivity",
      "choices",
      "blocks",
    ]) &&
    isString(data.messageId) &&
    (data.generationId === undefined || isString(data.generationId)) &&
    isInt(data.messageSeq) &&
    isString(data.text) &&
    isSensitivity(data.sensitivity) &&
    data.redacted === undefined
  );
}
function isMessageCompletedRedacted(data: unknown): data is MessageCompletedRedacted {
  return (
    isObject(data) &&
    hasOnlyKeys(data, [
      "messageId",
      "generationId",
      "messageSeq",
      "text",
      "sensitivity",
      "redacted",
    ]) &&
    isString(data.messageId) &&
    (data.generationId === undefined || isString(data.generationId)) &&
    isInt(data.messageSeq) &&
    data.text === null &&
    isSensitivity(data.sensitivity) &&
    data.redacted === true
  );
}

function buildDurableFrame(raw: Obj, type: DurableEventType): DurableEvent | null {
  if (!(isInt(raw.seq) && raw.seq >= 0)) return null;
  const data = raw.data;

  switch (type) {
    case "run.queued":
      return isRunQueuedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "run.started":
      return isRunStartedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "run.requeued":
      return isRunRequeuedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "run.completed":
      return isRunCompletedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "run.failed":
      return isRunFailedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "run.cancelled":
      return isRunCancelledData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "run.expired":
      return isRunExpiredData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "model.completed":
      return isModelCompletedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "tool.completed":
      return isToolCompletedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "tool.failed":
      return isToolFailedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "confirmation.requested":
      return isProposalView(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "confirmation.resolved":
      return isConfirmationResolvedData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "confirmation.expired":
      return isConfirmationExpiredData(data) || isRedactedMarker(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
    case "message.completed":
      return isMessageCompletedData(data) || isMessageCompletedRedacted(data)
        ? { schemaVersion: 1, seq: raw.seq, type, data }
        : null;
  }
}

/**
 * Validate + narrow a raw parsed frame. Returns a discriminated result the
 * reducer maps to duplicate/apply/recover.
 */
export function validateFrame(raw: unknown): ValidateFrameResult {
  if (!isObject(raw)) return { ok: false, reason: "schema-invalid" };
  if (raw.schemaVersion !== 1) return { ok: false, reason: "schema-invalid" };

  if (!("type" in raw)) {
    // A1 — legacy snapshot cards ({id, tool}) normalize to the CC-426 shape
    // before the strict guard runs.
    if (isObject(raw.state) && Array.isArray(raw.state.toolCards)) {
      for (const card of raw.state.toolCards) normalizeLegacyToolPayload(card);
    }
    if (
      isString(raw.runId) &&
      isInt(raw.throughSeq) &&
      raw.throughSeq >= -1 &&
      isSnapshotState(raw.state)
    ) {
      const frame: SnapshotFrame = {
        schemaVersion: 1,
        runId: raw.runId,
        throughSeq: raw.throughSeq,
        state: raw.state,
      };
      return { ok: true, frame };
    }
    return { ok: false, reason: "schema-invalid" };
  }

  const type = raw.type;
  if (!isString(type)) return { ok: false, reason: "schema-invalid" };

  // CC-426 live-only step start — seq-less like message.delta, never durable.
  if (type === LIVE_TOOL_STARTED_TYPE) {
    if (isToolStartedData(raw.data)) {
      return {
        ok: true,
        frame: {
          schemaVersion: 1,
          type: LIVE_TOOL_STARTED_TYPE,
          data: { invocationId: raw.data.invocationId, label: raw.data.label },
        },
      };
    }
    return { ok: false, reason: "schema-invalid" };
  }

  // A1 — legacy tool payloads normalize before the durable guards run.
  if (type === "tool.completed" || type === "tool.failed") {
    normalizeLegacyToolPayload(raw.data);
  }

  if (type === LIVE_DELTA_TYPE) {
    const data = raw.data;
    if (
      isObject(data) &&
      isString(data.messageId) &&
      isString(data.generationId) &&
      isInt(data.streamOrdinal) &&
      data.streamOrdinal >= 0 &&
      isString(data.text)
    ) {
      const frame: LiveDelta = {
        schemaVersion: 1,
        type: LIVE_DELTA_TYPE,
        data: {
          messageId: data.messageId,
          generationId: data.generationId,
          streamOrdinal: data.streamOrdinal,
          text: data.text,
        },
      };
      return { ok: true, frame };
    }
    return { ok: false, reason: "schema-invalid" };
  }

  if (!isDurableEventType(type)) return { ok: false, reason: "unknown-type" };
  const frame = buildDurableFrame(raw, type);
  if (frame === null) return { ok: false, reason: "schema-invalid" };
  return { ok: true, frame };
}

// ---------------------------------------------------------------------------
// Frame-family guards (used by the reducer to dispatch a validated frame)
// ---------------------------------------------------------------------------

/** A snapshot frame is the only member without a `type` discriminant. */
export function isSnapshotFrame(frame: AssistantFrame): frame is SnapshotFrame {
  return !("type" in frame);
}

export function isLiveDelta(frame: AssistantFrame): frame is LiveDelta {
  return "type" in frame && frame.type === LIVE_DELTA_TYPE;
}

export function isLiveToolStarted(frame: AssistantFrame): frame is LiveToolStarted {
  return "type" in frame && frame.type === LIVE_TOOL_STARTED_TYPE;
}

export function isDurableEvent(frame: AssistantFrame): frame is DurableEvent {
  return "type" in frame && frame.type !== LIVE_DELTA_TYPE && frame.type !== LIVE_TOOL_STARTED_TYPE;
}
